/**
 * Marketing tracking for the server-side Google Ads / GA4 conversions.
 *
 * What it does, and only with the visitor's real Cookiebot choice:
 *  1. Remembers the landing page tracking parameters (gclid, utm_*, ...) before the SPA changes the URL.
 *  2. Sends them, the Cookiebot consent and the GA4 client/session ids to POST /api/tracking/context.
 *     The backend keeps them under an HttpOnly cookie; nothing is stored in localStorage.
 *  3. Links a newly placed / paid order to this browser (POST /api/tracking/orders/{id}/link).
 *  4. Fires the browser Google Ads conversion with the server-confirmed price (the backend sends the
 *     same conversion, same transaction id, through the Data Manager API; Google merges the two).
 *  5. Diagnostics: tells the backend whether that browser conversion really left the page and with which
 *     transaction id (POST /api/tracking/orders/{id}/ads-tag). Nothing extra is sent to Google.
 *
 * Every call is fire-and-forget: tracking can never block or break the checkout.
 */
import Constants from '../../constants/APIConstants';
import * as StorageService from '../StorageService';

export const GA4_MEASUREMENT_ID = 'G-2PGH8RBNKV';
export const ADS_PURCHASE_SEND_TO = 'AW-840657989/hgCQCPyKlacdEMXY7ZAD';

const LANDING_PARAMS = ['gclid', 'gbraid', 'wbraid', 'gad_source', 'gad_campaignid',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const GTAG_GET_TIMEOUT_MS = 1500;
const ADS_ACCOUNT_ID = 'AW-840657989';
const ADS_PURCHASE_LABEL = ADS_PURCHASE_SEND_TO.split('/')[1];
// gtag calls event_callback once the hit is sent, or after event_timeout; if gtag.js never loaded
// (blocked), it never calls it, so the fallback reports anyway.
const ADS_EVENT_TIMEOUT_MS = 2000;
const ADS_REPORT_FALLBACK_MS = 4000;
// Lets the conversion request show up in the page's resource timing entries before they are read.
const ADS_HIT_SETTLE_MS = 1000;
// Cookiebot loads asynchronously; on the payment return page its answer may arrive after the payment check.
const CONSENT_WAIT_MS = 5000;
const STAFF_ROLES = ['ROLE_ADMIN', 'ROLE_MODERATOR', 'ROLE_LAWYER'];
const PLACEHOLDER_EMAIL = 'default@caziere.ro';
const COOKIEBOT_EVENTS = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'];

let landing = captureLanding();
let landingSent = false;
let started = false;
let syncQueue = Promise.resolve();

/** Keeps only the tracking parameters of the landing URL (no personal data from other parameters). */
export function captureLanding(win = typeof window === 'undefined' ? undefined : window) {
    if (!win || !win.location) return null;
    const url = new URL(win.location.href);
    const kept = new URLSearchParams();
    LANDING_PARAMS.forEach(name => {
        const value = url.searchParams.get(name);
        if (value) kept.set(name, value);
    });
    const query = kept.toString();
    return {
        url: url.origin + url.pathname + (query ? '?' + query : ''),
        referrer: win.document && win.document.referrer ? win.document.referrer : null
    };
}

/** Starts listening to Cookiebot. Call once, when the app starts. */
export function startTracking() {
    if (started || typeof window === 'undefined') return;
    started = true;
    COOKIEBOT_EVENTS.forEach(name => window.addEventListener(name, () => { syncTracking(); }));
    if (window.Cookiebot && window.Cookiebot.hasResponse === true) {
        syncTracking();
    }
}

/**
 * The visitor's Cookiebot decision, or null while Cookiebot has no answer yet.
 * null is NOT a refusal: nothing is sent, so an unknown state can never erase earlier data.
 */
export function readConsent() {
    const cookiebot = typeof window === 'undefined' ? undefined : window.Cookiebot;
    if (!cookiebot || cookiebot.hasResponse !== true || !cookiebot.consent) return null;
    return {
        analytics: cookiebot.consent.statistics === true,
        marketing: cookiebot.consent.marketing === true
    };
}

/**
 * Sends the current consent (+ landing params once, + GA4 ids) to the backend.
 * Calls run one after another, so the first response's cookie exists before the next call (e.g. an order link).
 */
export function syncTracking() {
    syncQueue = syncQueue.then(doSync, doSync);
    return syncQueue;
}

async function doSync() {
    try {
        if (isStaffSession()) return;
        const consent = readConsent();
        if (!consent) return;
        const body = {consent};
        if (!landingSent && landing && (consent.analytics || consent.marketing)) {
            body.landing = landing;
        }
        if (consent.analytics) {
            body.ga = await readGaIds();
        }
        const response = await post('/tracking/context', body);
        if (response && response.ok && body.landing) landingSent = true;
    } catch (error) {
        console.warn('[Tracking] context sync failed', error);
    }
}

/**
 * Links an order to this browser so its paid conversion can carry the ad click / GA4 ids.
 * @param orderId the order id
 * @param proof   {auth: jwtResponse} right after placing the order, or {code} from the payment URL
 */
export async function linkOrderToTracking(orderId, proof = {}) {
    try {
        if (orderId === undefined || orderId === null || isStaffSession()) return;
        const consent = readConsent();
        if (!consent || (!consent.analytics && !consent.marketing)) return;
        await syncTracking();
        const headers = {};
        if (proof.auth && proof.auth.token) {
            headers.Authorization = `${proof.auth.type || 'Bearer'} ${proof.auth.token}`;
        }
        await post(`/tracking/orders/${encodeURIComponent(orderId)}/link`, proof.code ? {code: proof.code} : {}, headers);
    } catch (error) {
        console.warn('[Tracking] order link failed', error);
    }
}

/**
 * Resolves with the visitor's Cookiebot choice as soon as it is known, or null if it is still unknown
 * after {@code timeoutMs} (unknown is never treated as consent).
 */
export function whenConsentKnown(timeoutMs = CONSENT_WAIT_MS) {
    const known = readConsent();
    if (known || typeof window === 'undefined') return Promise.resolve(known);
    return new Promise(resolve => {
        let timer;
        const done = consent => {
            clearTimeout(timer);
            COOKIEBOT_EVENTS.forEach(name => window.removeEventListener(name, onCookiebot));
            resolve(consent);
        };
        const onCookiebot = () => {
            const consent = readConsent();
            if (consent) done(consent);
        };
        COOKIEBOT_EVENTS.forEach(name => window.addEventListener(name, onCookiebot));
        timer = setTimeout(() => done(readConsent()), timeoutMs);
    });
}

/**
 * Browser Google Ads purchase conversion, only after the backend confirmed the payment and only with
 * Cookiebot "marketing" consent (the same rule as the backend). Uses the server price and adds
 * enhanced-conversion data (gtag hashes it before sending).
 */
export function reportBrowserPurchase({orderId, value, email, phone, code}) {
    const consent = readConsent();
    if (!consent || !consent.marketing) {
        console.info('[Google Ads] No marketing consent; browser conversion not sent for order', orderId);
        return false;
    }
    const gtag = typeof window === 'undefined' ? undefined : window.gtag;
    if (typeof gtag !== 'function') {
        console.error('[Google Ads] gtag is not available; the backend will still report order', orderId);
        return false;
    }
    const price = Number(value);
    if (orderId === undefined || orderId === null || !Number.isFinite(price) || price <= 0) {
        console.error('[Google Ads] Missing confirmed order id/value; browser conversion not sent.');
        return false;
    }
    try {
        const userData = enhancedConversionData(email, phone);
        if (userData) gtag('set', 'user_data', userData);
        let reported = false;
        const report = () => {
            if (reported) return;
            reported = true;
            setTimeout(() => reportBrowserTag(orderId, code), ADS_HIT_SETTLE_MS);
        };
        gtag('event', 'conversion', {
            send_to: ADS_PURCHASE_SEND_TO,
            value: price,
            currency: 'RON',
            transaction_id: String(orderId),
            event_callback: report,
            event_timeout: ADS_EVENT_TIMEOUT_MS
        });
        setTimeout(report, ADS_REPORT_FALLBACK_MS);
        if (userData) gtag('set', 'user_data', {});
        return true;
    } catch (error) {
        console.error('[Google Ads] Browser conversion failed', error);
        return false;
    }
}

/**
 * What this page can see of the browser purchase conversion: whether gtag.js really loaded and the
 * transaction id ("oid") of the conversion request it sent. Only the order id leaves the page.
 */
export function inspectBrowserTag(win = window) {
    const tagLoaded = !!(win.google_tag_manager && win.google_tag_manager[ADS_ACCOUNT_ID]);
    let hits = [];
    try {
        hits = win.performance.getEntriesByType('resource')
            .map(entry => entry.name)
            .filter(name => name.includes(ADS_PURCHASE_LABEL));
    } catch (error) { /* no resource timing */ }
    let hitTransactionId = null;
    hits.forEach(name => {
        try {
            hitTransactionId = new URL(name).searchParams.get('oid') || hitTransactionId;
        } catch (error) { /* not a URL */ }
    });
    return {tagLoaded, hitSeen: hits.length > 0, hitTransactionId};
}

export async function reportBrowserTag(orderId, code) {
    try {
        if (!code || isStaffSession()) return;
        await post(`/tracking/orders/${encodeURIComponent(orderId)}/ads-tag`, {code, ...inspectBrowserTag()});
    } catch (error) {
        console.warn('[Tracking] browser tag report failed', error);
    }
}

export function enhancedConversionData(email, phone) {
    const data = {};
    const cleanEmail = typeof email === 'string' ? email.trim().toLowerCase() : '';
    if (cleanEmail && cleanEmail !== PLACEHOLDER_EMAIL && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleanEmail)) {
        data.email = cleanEmail;
    }
    const e164 = toE164(phone);
    if (e164) data.phone_number = e164;
    return Object.keys(data).length > 0 ? data : null;
}

/** Same rules as the backend: Romanian national formats become +40..., anything ambiguous is dropped. */
export function toE164(phone) {
    if (typeof phone !== 'string') return null;
    let digits = phone.trim().replace(/[\s\-().]/g, '');
    if (digits.startsWith('00')) digits = '+' + digits.substring(2);
    else if (/^0[1-9]\d{8}$/.test(digits)) digits = '+40' + digits.substring(1);
    else if (/^40[1-9]\d{8}$/.test(digits)) digits = '+' + digits;
    return /^\+[1-9]\d{7,14}$/.test(digits) ? digits : null;
}

/** Reads the real GA4 ids from the Google tag. Each id is optional; nothing is ever invented. */
function readGaIds() {
    const gtag = window.gtag;
    if (typeof gtag !== 'function') return Promise.resolve({});
    const read = field => new Promise(resolve => {
        const timer = setTimeout(() => resolve(undefined), GTAG_GET_TIMEOUT_MS);
        try {
            gtag('get', GA4_MEASUREMENT_ID, field, value => {
                clearTimeout(timer);
                resolve(value === undefined || value === null ? undefined : String(value));
            });
        } catch (error) {
            clearTimeout(timer);
            resolve(undefined);
        }
    });
    return Promise.all([read('client_id'), read('session_id')])
        .then(([clientId, sessionId]) => ({clientId, sessionId}));
}

function isStaffSession() {
    const auth = StorageService.getToken();
    const roles = auth && Array.isArray(auth.roles) ? auth.roles : [];
    return roles.some(role => STAFF_ROLES.includes(role));
}

function post(path, body, headers = {}) {
    return fetch(Constants.API.HOST_AND_PORT + path, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: {'Content-Type': 'application/json', ...headers},
        body: JSON.stringify(body)
    });
}

/** For tests only. */
export function resetTrackingForTests(win) {
    landing = captureLanding(win);
    landingSent = false;
    started = false;
    syncQueue = Promise.resolve();
}
